import { BrandName } from "@/components/BrandName";
import {
  AlfredIcon,
  InvoicesIcon,
  SettingsIcon,
  UsageIcon,
} from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link, useRouterState } from "@tanstack/react-router";
import { cn } from "cn";
import type { ComponentType } from "react";

import { useSession } from "./session.tsx";
import {
  canOpenSection,
  DASHBOARD_SECTIONS,
  type DashboardSection,
} from "./workspace.ts";

/**
 * Un icono por entrada, y ninguno suelto.
 *
 * Están aquí y no en `workspace.ts` por lo mismo que los del conmutador:
 * aquello dice a dónde lleva cada sección, esto dice cómo se dibuja.
 */
const SECTION_ICONS: Record<
  DashboardSection["key"],
  ComponentType<{ className?: string }>
> = {
  invoices: InvoicesIcon,
  usage: UsageIcon,
  alfred: AlfredIcon,
};

/**
 * La columna izquierda del panel: sus secciones y nada más.
 *
 * Ni historial ni memoria: eso es del chat y aquí no significaría nada. Mismo
 * ancho que la del chat a propósito --si no, el conmutador centrado baila al
 * cambiar de modo, que es justo el mueble que no puede moverse--.
 *
 * Los Ajustes van abajo y separados porque no son una sección del panel: es el
 * mismo diálogo que abre el engranaje del chat.
 */
export function DashboardSidebar({ onSettings }: { onSettings: () => void }) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const session = useSession();

  return (
    <aside className="flex w-72 shrink-0 flex-col gap-1 border-r bg-card p-3">
      <BrandName className="mb-2" />

      {/* Una sección que no se puede leer no se ofrece: llevaría a una
          pantalla que solo sabe decir que no. */}
      {DASHBOARD_SECTIONS.filter((section) =>
        canOpenSection(section, session),
      ).map((section) => {
        const active = pathname.startsWith(section.path);
        const Icon = SECTION_ICONS[section.key];

        return (
          <Link
            key={section.key}
            to={section.path}
            search={(previous) => previous}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors hover:bg-secondary",
              active && "bg-secondary font-medium",
            )}
          >
            <Icon className="size-4 text-muted-foreground" aria-hidden />
            {section.label}
          </Link>
        );
      })}

      <Separator className="my-3" />

      <Button
        variant="ghost"
        size="sm"
        className="justify-start font-normal"
        onClick={onSettings}
      >
        <SettingsIcon /> Ajustes
      </Button>
    </aside>
  );
}
